import React, {  RefObject, useLayoutEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import './DragManager.css';
import { GRID_SIZE } from './Grid';
import DragToken from '../Token/DragToken';
import DragMapImage from '../MapImages/DragMapImage';
import { DraggableObject, dragSelector } from '../../reducers/drag/dragReducer';
import DragSpell from '../Spells/DragSpell';
import { createSelector } from '@reduxjs/toolkit';
import { zoomSelector } from '../../reducers/zoom/zoomReducer';

const dragElementMap = {
    'move-token': DragToken,
    'move-map-image': DragMapImage,
    'move-spell': DragSpell,
};

const getSize = (details: DraggableObject ) => {
    switch (details?.type) {
    case 'move-token':
        return {
            width: details.details.token.width * GRID_SIZE,
            height: details.details.token.height * GRID_SIZE,
        };
    case 'move-map-image':
        return {
            width: details.details.image.width,
            height: details.details.image.height,
        };
    case 'move-spell':
        return {
            width: details.details.shape.width * 5,
            height: details.details.shape.height * 5,
        };
    default:
        return {
            width: GRID_SIZE,
            height: GRID_SIZE,
        };
    }

};

const selector = createSelector([dragSelector, zoomSelector], (drag, zoom) => ({
    isDragging: drag.dragging,
    dragInfo: drag.draggedObject,
    zoom: zoom.zoom,
}));

const DragManager = ({ containerRef }: { containerRef: RefObject<HTMLDivElement>}) => {

    const dispatch = useDispatch();
    const [style, setStyle] = useState({});


    const { isDragging, dragInfo, zoom } = useSelector(selector, shallowEqual);

    useLayoutEffect(() => {
        if(isDragging) {
            const { width, height } = getSize(dragInfo);
            setStyle({
                top: dragInfo?.details.startY,
                left: dragInfo?.details.startX,
                width: `${width}px`,
                height: `${height}px`,
                transform: `translate(-50%, -50%) scale(${zoom})`,
            });
            const onMouseMove = (e: MouseEvent) => {
                setStyle({
                    top: e.clientY,
                    left: e.pageX,
                    width: `${width}px`,
                    height: `${height}px`,
                    transform: `translate(-50%, -50%) scale(${zoom})`,
                });
            };
            window.addEventListener('mousemove', onMouseMove);
            return () => {
                window.removeEventListener('mousemove', onMouseMove);
            };
        }
    }, [isDragging, dispatch, containerRef, dragInfo, zoom]);

    // eslint-disable-next-line
    const Element = dragInfo ? dragElementMap[dragInfo.type] : null;
    return isDragging && dragInfo && Element
        ? ReactDOM.createPortal(
            (
                <Element
                    className='dragged-token'
                    style={style}
                    dragInfo={dragInfo}
                />
            ),
            containerRef.current as HTMLDivElement,
        ) : null;
};

export default DragManager;
